import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import type { MailFolder } from "@/features/mail/mail-folders";

/**
 * The mailbox's data (§17A.1) — ONE READ, AND NOTHING ELSE.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS DELIBERATELY DOES NOT DO, BECAUSE THE HONEST SCOPE IS SMALL.
 *
 * NO IMAP POLLING, AND NO POLLING OF ANY KIND. `mail_messages` rows exist only
 * for what this app SENDS. Nothing fetches from the server's IMAP side, so there
 * is no stream of new mail for an interval to keep up with, and a 30-second poll
 * would be a request twice a minute on a metered workshop connection that can
 * only ever return what the reader's own last send put there. `refetchOnFocus`
 * is on globally (`lib/query-client.ts`), which is exactly the right amount of
 * liveness for a list that changes when — and only when — this person acts.
 *
 * NO UNREAD BADGE ON THE RAIL ROW. The badge on the bell and the one on the
 * messenger both count things somebody ELSE did. Nothing arrives here from
 * anybody else, so a badge would count the reader's own sent mail as "unread"
 * and never mean anything. The row is a door, not a notification.
 *
 * NO MUTATIONS AT ALL. There is no `mail.delete`, no `mail.edit` and no
 * `mail.mark_read` in §2.1's union, deliberately (see `permissions.ts`), so
 * there is nothing here for a mutation to call.
 *
 * The unread FILTER below is still worth having: `is_read` is a real column that
 * an IMAP sync will populate, and the toggle is `sidebar-09`'s. It filters what
 * is shown; it never writes.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * `GET /mail/messages`, spelled here rather than in `lib/endpoints.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT BELONGS IN `lib/endpoints.ts` AND SHOULD MOVE THERE. It is local only
 * because that file is held open by the send half of this feature (§17A.2), and
 * two workflows editing one 2,000-line module is how a route gets lost in a
 * merge. This is the arrangement `challanKeys` and `paymentKeys` were in before
 * 2026-08-16, with the same instruction attached: fold it in, at the same VALUE,
 * so no cache entry is orphaned by the move.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * BEHIND `requireFlag("mail")`, WHICH SHIPS OFF, so this answers **404** until
 * the module is switched on (§18.2) — see {@link isModuleOff}. It is a READ, so
 * `requireCap("mail.view")` is what guards it once the flag is on; the send half
 * is a different route behind a different capability.
 */
export const MAIL_MESSAGES_ENDPOINT = "/mail/messages";

/**
 * The mailbox's cache prefix.
 *
 * ITS OWN PREFIX, NOT A SLICE OF `messages`. `queryKeys.messages` is the INTERNAL
 * conversation; this is the email record. They share a word in English and
 * nothing else — one table each, one counterparty each — and a shared prefix
 * would mean sending a chat message invalidated the customer correspondence
 * list, or worse, that a future `invalidateQueries({queryKey: ["messages"]})`
 * quietly refetched both.
 *
 * Declared here for the reason {@link MAIL_MESSAGES_ENDPOINT} is; it belongs in
 * `lib/query-keys.ts` and moves there at the same values.
 */
export const mailKeys = {
  all: ["mail"] as const,
  lists: () => ["mail", "list"] as const,
  list: (folder: MailFolder, unreadOnly: boolean) => ["mail", "list", { folder, unreadOnly }] as const,
};

/**
 * One row of a folder, as `GET /mail/messages` returns it.
 *
 * TRANSCRIBED FROM `MailMessage` IN `schema.prisma`, with two departures that
 * are both about what a LIST needs:
 *
 *  • `preview` RATHER THAN `bodyText`. The column is `LongText`; fifty of them
 *    is a page nobody reads, on a connection the rest of this app is careful
 *    about. The server truncates.
 *  • NO `bodyHtml`, EVER, ON THIS SHAPE. Nothing in this feature renders HTML —
 *    every string below goes through JSX, which React escapes by construction,
 *    and there is no `dangerouslySetInnerHTML` in this directory. Not carrying
 *    the field is stronger than carrying it and remembering not to use it.
 */
export interface MailMessageRow {
  id: number;
  folder: MailFolder;
  /** Absent on a message with no subject line; the screen says so rather than
   *  rendering an empty heading. */
  subject: string | null;
  fromAddress: string;
  fromName: string | null;
  /** Comma-separated, exactly as stored — displayed whole, never queried into. */
  toAddresses: string;
  /** Outbound. `null` until the SMTP handoff succeeded. */
  sentAt: string | null;
  /** Inbound. `null` on everything this app sent. */
  receivedAt: string | null;
  isRead: boolean;
  hasAttachments: boolean;
  /** The first line or two of the body, truncated by the server. */
  preview: string | null;
}

/** `GET /mail/messages` — envelope key `messages`, plus the folder's counts. */
export interface MailListResponse {
  messages: MailMessageRow[];
  /** How many are in this folder in total, before the unread filter. */
  total: number;
  /** How many of them are unread. */
  unread: number;
}

/**
 * Whether the module exists on this server at all.
 *
 * THERE IS NO `useFlag`, AND THIS FEATURE DOES NOT INVENT ONE — `GET /flags`
 * needs `settings.write`, which is ADMIN-only, so a client-side flag check is
 * unanswerable for the OWNER, who is the person the mailbox is for. §18.2 makes
 * a disabled feature a **404, not a 403**, so the 404 IS the answer. Challans,
 * credit notes, payments, notifications and the messenger all read their flags
 * exactly this way.
 *
 * A 404 THEREFORE MEANS "SWITCHED OFF", NEVER "NOTHING TO SHOW" — the screen
 * renders `FeatureOff` rather than an empty folder, which would promise mail
 * that cannot arrive.
 */
export function isModuleOff(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export type MailboxQuery = UseQueryResult<MailListResponse, Error>;

/**
 * One folder's messages.
 *
 * `enabled` IS PASSED IN RATHER THAN READ HERE, the convention `useMessagesUnread`
 * set: the capability check lives in the component beside the thing it hides, and
 * the hook can be exercised on its own. Called with `false` it makes NO request —
 * a reader without `mail.view` must not fire a request that would 403, because
 * `requireCap` writes every denial to `audit_log` (§2.1) and a security log full
 * of denials nobody chose is a security log nobody reads.
 */
export function useMailbox(
  folder: MailFolder,
  unreadOnly: boolean,
  enabled = true,
): MailboxQuery {
  return useQuery({
    queryKey: mailKeys.list(folder, unreadOnly),
    queryFn: () =>
      api.get<MailListResponse>(MAIL_MESSAGES_ENDPOINT, {
        query: {
          folder,
          // Omitted entirely when off, rather than sent as `false`: the API's
          // default is "everything", and two spellings of the same request would
          // be two cache entries on the server's side of any future caching.
          unread: unreadOnly ? true : undefined,
        },
      }),
    enabled,
  });
}
